/**
 * Learn API client — public reads + auth'd writes.
 *
 * Auth: uses the same `X-Auth-Token` produced by
 *   POST /api/pp/auth/user-pin   { pin, section: "learn_edit" }
 * Token is kept in sessionStorage under "learn_edit_token".
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "learn_edit_token";

export const getToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
};
export const setToken = (t) => {
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
};
export const clearToken = () => setToken("");

// ---------- Public ----------
export const listCategories = () =>
  axios.get(`${API}/learn/categories`).then((r) => r.data);

export const listVideos = ({ includeUnpublished = false, language } = {}) => {
  const params = {};
  if (includeUnpublished) params.include_unpublished = true;
  if (language) params.language = language;
  return axios.get(`${API}/learn/videos`, { params }).then((r) => r.data);
};

export const streamUrlFor = (videoId) =>
  `${API}/learn/stream/${videoId}`;

// ---------- Auth ----------
export const loginWithPin = async (pin) => {
  const { data } = await axios.post(`${API}/pp/auth/user-pin`, {
    pin,
    section: "learn_edit",
  });
  if (data.valid && data.token) {
    setToken(data.token);
    return { ok: true, user: data.user };
  }
  return { ok: false, reason: data.reason || "invalid" };
};

// ---------- Admin ----------
const authHeaders = () => ({ "X-Auth-Token": getToken() });

// Chunk size for large-file uploads. Kept comfortably under the ~25 MB
// Kubernetes ingress body-size cap seen on the production deployment.
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB
// Files smaller than this go through the single-shot /upload endpoint.
const SINGLE_SHOT_MAX = 8 * 1024 * 1024; // 8 MB

export const uploadFile = async (file, { onProgress } = {}) => {
  // -- Small file: original single-shot path --
  if (file.size <= SINGLE_SHOT_MAX) {
    const fd = new FormData();
    fd.append("file", file);
    const r = await axios.post(`${API}/learn/upload`, fd, {
      headers: { ...authHeaders() },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return r.data;
  }

  // -- Large file: chunked upload (init → chunk × N → complete) --
  const initRes = await axios.post(
    `${API}/learn/upload/init`,
    { filename: file.name, total_bytes: file.size },
    { headers: authHeaders() },
  );
  const uploadId = initRes.data.upload_id;

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let bytesSent = 0;

  try {
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("file", chunk, `chunk_${i}`);
      await axios.post(`${API}/learn/upload/chunk`, fd, {
        headers: authHeaders(),
        onUploadProgress: (e) => {
          if (!onProgress || !file.size) return;
          const chunkBytes = typeof e.loaded === "number" ? e.loaded : 0;
          const totalDone = bytesSent + chunkBytes;
          const pct = Math.min(99, Math.round((totalDone / file.size) * 100));
          onProgress(pct);
        },
      });
      bytesSent += end - start;
    }
    const completeRes = await axios.post(
      `${API}/learn/upload/complete`,
      { upload_id: uploadId },
      { headers: authHeaders() },
    );
    onProgress?.(100);
    return completeRes.data;
  } catch (err) {
    // Best-effort server-side cleanup so the pod's tmp dir doesn't leak.
    axios
      .post(
        `${API}/learn/upload/abort`,
        { upload_id: uploadId },
        { headers: authHeaders() },
      )
      .catch(() => {});
    throw err;
  }
};

export const createVideo = (payload) =>
  axios.post(`${API}/learn/videos`, payload, { headers: authHeaders() }).then((r) => r.data);

export const updateVideo = (id, patch) =>
  axios.put(`${API}/learn/videos/${id}`, patch, { headers: authHeaders() }).then((r) => r.data);

export const deleteVideo = (id) =>
  axios.delete(`${API}/learn/videos/${id}`, { headers: authHeaders() }).then((r) => r.data);

// ---------- Helpers ----------
export const isYouTubeUrl = (url) =>
  /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/.test(url || "");

export const youTubeIdFrom = (url) => {
  const m = (url || "").match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );
  return m ? m[1] : "";
};

export const isVimeoUrl = (url) => /vimeo\.com\/(?:video\/)?\d+/.test(url || "");

export const vimeoIdFrom = (url) => {
  const m = (url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : "";
};

export const youTubeThumb = (url) => {
  const id = youTubeIdFrom(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
};

export const formatDuration = (secs) => {
  if (!secs || secs <= 0) return "";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};
